// Green-screen keying (CPU version used for full-resolution captures; the live
// stage uses the identical maths in the WebGL shader, see render/shaders.ts).

export interface ChromaParams {
  /** key colour, 0..1 */
  key: [number, number, number];
  /** chroma distance below which a pixel is fully transparent */
  similarity: number;
  /** soft edge width */
  smoothness: number;
  /** green-spill suppression 0..1 */
  spill: number;
}

export const DEFAULT_CHROMA: ChromaParams = {
  key: [0.0, 1.0, 0.0],
  similarity: 0.32,
  smoothness: 0.1,
  spill: 0.6,
};

const toUV = (r: number, g: number, b: number): [number, number] => [
  -0.169 * r - 0.331 * g + 0.5 * b,
  0.5 * r - 0.419 * g - 0.081 * b,
];

/** Alpha 0..1 for one pixel (all values 0..1). */
export function chromaAlpha(r: number, g: number, b: number, p: ChromaParams = DEFAULT_CHROMA) {
  const [ku, kv] = toUV(...p.key);
  const [u, v] = toUV(r, g, b);
  const d = Math.hypot(u - ku, v - kv);
  const t = (d - p.similarity) / Math.max(1e-4, p.smoothness);
  return Math.max(0, Math.min(1, t));
}

/** In-place keying of RGBA pixels. */
export function chromaKeyPixels(px: Uint8ClampedArray, p: ChromaParams = DEFAULT_CHROMA) {
  for (let i = 0; i < px.length; i += 4) {
    const r = px[i] / 255, g = px[i + 1] / 255, b = px[i + 2] / 255;
    const a = chromaAlpha(r, g, b, p);
    // spill suppression: clamp green to max(r,b)
    const cap = Math.max(r, b);
    const g2 = g > cap ? g - (g - cap) * p.spill : g;
    px[i + 1] = g2 * 255;
    px[i + 3] = a * 255;
  }
}
